import * as React from "react";

import {
  CONSENT_VERSION,
  createConsent,
  setConsentCookie,
  type ConsentCategories,
  type ConsentState,
  type CookieConsentContextValue,
} from "src/lib/cookieConsent";

interface CookieConsentState {
  consent: ConsentState | null;
  isBannerVisible: boolean;
}

type CookieConsentAction =
  | { type: "ACCEPT_ALL" }
  | { type: "REJECT_ALL" }
  | { type: "ACCEPT_SELECTED"; payload: { categories: ConsentCategories } }
  | { type: "RESET_CONSENT" };

function createInitialState(
  serverConsent: ConsentState | null,
): CookieConsentState {
  const hasValidConsent =
    serverConsent && serverConsent.version >= CONSENT_VERSION;
  return {
    consent: hasValidConsent ? serverConsent : null,
    isBannerVisible: !hasValidConsent,
  };
}

function cookieConsentReducer(
  state: CookieConsentState,
  action: CookieConsentAction,
): CookieConsentState {
  switch (action.type) {
    case "ACCEPT_ALL": {
      return {
        ...state,
        consent: createConsent({
          essential: true,
          functional: true,
          analytics: true,
        }),
        isBannerVisible: false,
      };
    }

    case "REJECT_ALL": {
      return {
        ...state,
        consent: createConsent({
          essential: true,
          functional: true,
          analytics: false,
        }),
        isBannerVisible: false,
      };
    }

    case "ACCEPT_SELECTED": {
      return {
        ...state,
        consent: createConsent(action.payload.categories),
        isBannerVisible: false,
      };
    }

    case "RESET_CONSENT": {
      return {
        ...state,
        isBannerVisible: true,
      };
    }

    default:
      return state;
  }
}

const CookieConsentContext =
  React.createContext<CookieConsentContextValue | null>(null);

interface CookieConsentProviderProps {
  children: React.ReactNode;
  initialConsent: ConsentState | null;
}

export function CookieConsentProvider(props: CookieConsentProviderProps) {
  const [state, dispatch] = React.useReducer(
    cookieConsentReducer,
    props.initialConsent,
    createInitialState,
  );

  const previousConsentRef = React.useRef(props.initialConsent);

  React.useEffect(() => {
    if (state.consent === previousConsentRef.current) {
      return;
    }

    if (state.consent) {
      setConsentCookie(state.consent);
    }

    previousConsentRef.current = state.consent;
  }, [state.consent]);

  function acceptAll() {
    dispatch({ type: "ACCEPT_ALL" });
  }

  function rejectAll() {
    dispatch({ type: "REJECT_ALL" });
  }

  function acceptSelected(categories: ConsentCategories) {
    dispatch({ type: "ACCEPT_SELECTED", payload: { categories } });
  }

  function resetConsent() {
    dispatch({ type: "RESET_CONSENT" });
  }

  const contextValue: CookieConsentContextValue = {
    consent: state.consent,
    isConsentGiven: state.consent !== null,
    isBannerVisible: state.isBannerVisible,
    acceptAll,
    rejectAll,
    acceptSelected,
    resetConsent,
  };

  return (
    <CookieConsentContext.Provider value={contextValue}>
      {props.children}
    </CookieConsentContext.Provider>
  );
}

export function useCookieConsent(): CookieConsentContextValue {
  const context = React.useContext(CookieConsentContext);

  if (!context) {
    throw new Error(
      "useCookieConsent must be used within a CookieConsentProvider",
    );
  }

  return context;
}
